import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseOddsPageFilterBetTypesConfig } from '@/utils/odds-page-filter-bet-types.util';

import type { LicSettingsData } from '@nf/types/site';
import type { ApiBackendInfo } from '@/hooks/query/athena/types';
import queryKeys from '@/constants/query-keys';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { WcPromotionFeatureIndex } from '@/features/wc-promotion-2026/hook';

const SYNC_PERIOD = 3 * 60 * 1000;
// 設定還沒載入時也要回傳同一個參考：useAvailableBetTypes 把它當 useMemo 依賴，
// 每次 render 給新的 [] 會讓 Players 分頁的 initializeFilterData 無限觸發（React #185）
const EMPTY_KERO_PLAYER_PROPS_BET_TYPES: string[] = [];

export const fetchLicSettings = async (apiBackendInfo: ApiBackendInfo): Promise<LicSettingsData> => {
	const response = await fetchComposer.postWithBearer(
		`${apiBackendInfo.url}/Config/GetLicSettings`
	)(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useLicSettings = () => {
	const apiBackendInfo = useApiBackendInfo();

	const { data, isLoading, isError, error, refetch, isFetched } = useQuery({
		queryKey: [queryKeys.GET_LIC_SETTINGS as string, apiBackendInfo.url, apiBackendInfo.token],
		queryFn: () => fetchLicSettings(apiBackendInfo),
		enabled: apiBackendInfo.isReady,
		refetchInterval: SYNC_PERIOD,
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: false,
		staleTime: 1000 * 30,
		cacheTime: 1000 * 60,
	});

	const featureLists = data?.sportsbook?.enableWcPromotionFeatureLists;
	const isWcFeatureEnabled = (index: number) => Boolean(featureLists?.includes(index));
	const oddsPageFilterBetTypesBySport = useMemo(
		() => parseOddsPageFilterBetTypesConfig(data?.sportsbook?.oddsPageFilterBetTypes),
		[data?.sportsbook?.oddsPageFilterBetTypes],
	);

	return {
		data,
		isLoading,
		isError,
		error,
		refetch,
		isFetched,
		enabledBetBuilderPlusSportIDs: data?.sportsbook?.enabledBetBuilderPlusSportIDs,
		nonSport: data?.nonSport,
		enableWcPromotionWeeklyLottery: isWcFeatureEnabled(WcPromotionFeatureIndex.WeeklyLottery),
		enableWcPromotionLuckySpin: isWcFeatureEnabled(WcPromotionFeatureIndex.LuckySpin),
		enableWcPromotionAfterParty: isWcFeatureEnabled(WcPromotionFeatureIndex.AfterParty),
		enableWcPromotionLobbyBanner: isWcFeatureEnabled(WcPromotionFeatureIndex.LobbyBanner),
		enableBBPlusMinorBetTypesSportIds: data?.sportsbook?.enableBBPlusMinorBetTypesSportIds,
		enableESoccerBattle: data?.nonSport?.enableESoccerBattle,
		enableESoccerBattlePromotion: data?.nonSport?.enableESoccerBattlePromotion,
		enablePasswordMigration: data?.nonSport?.enablePasswordMigration ?? false,
		enableKeroPlayerProps: data?.sportsbook?.enableKeroPlayerProps,
		EnableKeroPlayerPropsBetTypes: data?.sportsbook?.enableKeroPlayerPropsBetTypes ?? EMPTY_KERO_PLAYER_PROPS_BET_TYPES,
		enableVipProgramHint: data?.sportsbook?.enableVipProgramHint,
		enableWorldCupFeverPopup: data?.sportsbook?.enableWorldCupFeverPopup,
		enableFootballLeagueResumesPopup: data?.sportsbook?.enableFootballLeagueResumesPopup,
		enableFootballLeagueFixtureFrenzyPopup: data?.sportsbook?.enableFootballLeagueFixtureFrenzyPopup,
		enableCSSection: data?.sportsbook?.enableCSSection,
		enableCSSectionBanner: data?.sportsbook?.enableCSSectionBanner,
		oddsPageFilterBetTypesBySport,
		enableAIChat: data?.sportsbook?.enableAIChat,
		enableQuickBetWidgetInSchedule: data?.sportsbook?.enableQuickBetWidgetInSchedule,
		enableStreamerBanner: data?.sportsbook?.enableStreamerBanner,
		enablePromoStreamerFloatIcon: data?.sportsbook?.enablePromoStreamerFloatIcon,
		enableListPopupBanner: data?.sportsbook?.enableListPopupBanner,
		enablePromotionSurvey: data?.sportsbook?.enablePromotionSurvey,
		enableStatScore: data?.sportsbook?.enableStatScore,
	};
};
